
import { useState, useCallback, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Box,
  HStack,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Image,
  Link,
} from '@chakra-ui/react'
import { Upload, Trash2, Video, Music, FileText, File } from 'lucide-react'
import { MediaService } from '@/lib/services/mediaService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface MediaUploadProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface FileWithValidation extends File {
  preview?: string | null
  fileType?: 'image' | 'video' | 'audio' | 'application' | 'text' | 'other'
  errors?: string[]
}


// File validation options matching LibraryForm.jsx
const fileOptions = {
  accept: {
    image: { type: ['png', 'jpg', 'jpeg'] as string[], maxSize: 1024 * 2000 }, // 2MB
    video: { type: [] as string[], maxSize: 1024 * 5000 }, // 5MB
    audio: { type: [] as string[], maxSize: 1024 * 5000 }, // 5MB
    application: { type: [] as string[], maxSize: 1024 * 2000 }, // 2MB
    text: { type: [] as string[], maxSize: 1024 * 2000 }, // 2MB
  },
}

// Check if file is an image based on MIME type or extension (moved outside component)
const isImageFile = (file: File): boolean => {
  // Check MIME type first
  if (file.type.startsWith('image/')) {
    return true
  }
  
  // Fallback: check file extension
  const fileName = file.name.toLowerCase()
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.ico', '.tiff', '.tif']
  return imageExtensions.some(ext => fileName.endsWith(ext))
}

export function MediaUpload({ isOpen, onClose, onSuccess }: MediaUploadProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Store preview URLs separately to ensure they persist (Map: fileName -> previewUrl)
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map())
  const [accepted, setAccepted] = useState<FileWithValidation[]>([])
  const [rejected, setRejected] = useState<FileWithValidation[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const borderColor = useColorModeValue('gray.300', 'gray.600')
  const dragBg = useColorModeValue('blue.50', 'blue.900')

  // Validate file based on type and size
  const validateFile = (file: File): string[] => {
    const errors: string[] = []
    const fileType = file.type.split('/')[0]
    const fileExt = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
    const accept = fileOptions.accept

    if (!accept[fileType as keyof typeof accept]) {
      errors.push('Invalid file type.')
      return errors
    }

    const typeConfig = accept[fileType as keyof typeof accept]
    if (typeConfig && typeConfig.type.length > 0 && !typeConfig.type.includes(fileExt)) {
      errors.push('Invalid file type.')
    }

    if (file.size > typeConfig.maxSize) {
      errors.push('Invalid file size.')
    }

    return errors
  }

  // Get file type for icon display
  const getFileType = (file: File): 'image' | 'video' | 'audio' | 'application' | 'text' | 'other' => {
    if (isImageFile(file)) {
      return 'image'
    }
    
    const fileType = file.type.split('/')[0]
    if (['video', 'audio', 'application', 'text'].includes(fileType)) {
      return fileType as 'video' | 'audio' | 'application' | 'text'
    }
    return 'other'
  }


  // Filter and validate files
  const filterFiles = useCallback((files: FileList) => {
    Array.from(files).forEach((file) => {
      const errors = validateFile(file)
      const fileType = getFileType(file)
      
      // Generate preview - following Dropzone.jsx logic exactly
      let preview: string | null = null
      try {
        // Always create object URL first (like Dropzone.jsx line 60)
        preview = URL.createObjectURL(file)
        
        // Override preview for non-image files (like Dropzone.jsx lines 61-72)
        const fileMimeType = file.type ? file.type.split('/')[0] : ''
        const isImage = isImageFile(file) || fileMimeType === 'image'
        
        if (fileMimeType === 'video' || fileMimeType === 'audio' || 
            fileMimeType === 'application' || fileMimeType === 'text') {
          // For non-image files, revoke the blob URL and set to null
          // We'll use icon components instead
          URL.revokeObjectURL(preview)
          preview = null
        } else if (isImage && preview) {
          // For images, store the blob URL in state Map for persistence
          // Also ensure it's set on the file object (done below)
          // preview is guaranteed to be a string here (not null) since we just created it
          console.log('Storing preview URL for image:', file.name, preview, 'Type:', file.type)
          setPreviewUrls((prev) => {
            const newMap = new Map(prev)
            newMap.set(file.name, preview as string)
            return newMap
          })
        } else {
          // Unknown file type - revoke the blob URL
          if (preview) {
            URL.revokeObjectURL(preview)
          }
          preview = null
        }
      } catch (error) {
        console.error('Failed to create preview URL:', error)
        preview = null
      }
      
      // Create file with validation properties
      // Directly assign properties like Dropzone.jsx does (line 73: file.preview = preview)
      const fileWithValidation = file as FileWithValidation
      // For images, ensure preview is set on the file object AND in the Map
      // The Map is the source of truth, but file.preview is a fallback
      if (preview && (isImageFile(file) || file.type.startsWith('image/'))) {
        fileWithValidation.preview = preview
      } else {
        fileWithValidation.preview = preview || undefined
      }
      fileWithValidation.fileType = fileType
      fileWithValidation.errors = errors.length > 0 ? errors : undefined

      if (errors.length > 0) {
        setRejected((prev) => [...prev, fileWithValidation])
      } else {
        setAccepted((prev) => [...prev, fileWithValidation])
      }
    })
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    filterFiles(e.dataTransfer.files)
  }, [filterFiles])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      filterFiles(e.target.files)
    }
    // Reset input so same file can be selected again
    if (e.target) {
      e.target.value = ''
    }
  }

  const removeFile = (fileName: string, from: 'accepted' | 'rejected') => {
    // Revoke blob URL from state Map
    setPreviewUrls((prev) => {
      const newMap = new Map(prev)
      const previewUrl = newMap.get(fileName)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        newMap.delete(fileName)
      }
      return newMap
    })
    
    if (from === 'accepted') {
      setAccepted((prev) => prev.filter((item) => item.name !== fileName))
    } else {
      setRejected((prev) => prev.filter((item) => item.name !== fileName))
    }
  }

  const handleUpload = async () => {
    if (accepted.length === 0) {
      showError(new Error('Please select at least one valid file'))
      return
    }

    setIsUploading(true)

    try {
      await MediaService.uploadMedia({ files: accepted })

      showSuccess('Media uploaded successfully')
      // Clean up object URLs from state Map
      setPreviewUrls((prev) => {
        const newMap = new Map(prev)
        accepted.forEach((file) => {
          const previewUrl = newMap.get(file.name)
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
            newMap.delete(file.name)
          }
        })
        return newMap
      })
      setAccepted([])
      setRejected([])
      onSuccess?.()
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      // Clean up all object URLs from state Map
      setPreviewUrls((prev) => {
        prev.forEach((url: string) => {
          URL.revokeObjectURL(url)
        })
        return new Map()
      })
      setAccepted([])
      setRejected([])
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload Media</ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            {/* Drop Zone */}
            <Box
              w="100%"
              p={8}
              border="2px"
              borderStyle="dashed"
              borderColor={isDragging ? 'blue.500' : borderColor}
              borderRadius="md"
              bg={isDragging ? dragBg : colors.sectionBg}
              transition="all 0.2s"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              cursor="pointer"
              position="relative"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  opacity: 0,
                  cursor: 'pointer',
                }}
                disabled={isUploading}
              />
              <VStack spacing={2}>
                <Upload size={48} color={colors.textMuted} />
                <Text fontWeight="medium" color={colors.text}>
                  Drop files to upload
                </Text>
                <Text fontSize="sm" color={colors.textMuted}>
                  or
                </Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => fileInputRef.current?.click()}
                  isDisabled={isUploading}
                >
                  Select Files
                </Button>
              </VStack>
            </Box>

            {/* File Size Info */}
            <Text fontSize="sm" color={colors.textMuted}>
              Maximum upload file size: 2 MB (Images, Files), 5 MB (Videos, Audio).
            </Text>

            {/* Accepted Files */}
            {accepted.length > 0 && (
              <Card w="100%" size="sm">
                <CardHeader pb={2}>
                  <Text fontSize="sm" fontWeight="medium" color={colors.text}>
                    Accepted ({accepted.length})
                  </Text>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={2} align="stretch">
                    {accepted.map((file, index) => (
                      <MediaItem
                        key={index}
                        file={file}
                        type="accepted"
                        onRemove={removeFile}
                        colors={colors}
                        previewUrl={previewUrls.get(file.name) || file.preview}
                      />
                    ))}
                  </VStack>
                </CardBody>
                <CardFooter pt={2}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Upload size={16} />}
                    onClick={handleUpload}
                    isLoading={isUploading}
                    loadingText="Uploading..."
                  >
                    Upload
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Rejected Files */}
            {rejected.length > 0 && (
              <Card w="100%" size="sm" borderColor="red.300">
                <CardHeader pb={2}>
                  <Text fontSize="sm" fontWeight="medium" color="red.500">
                    Rejected ({rejected.length})
                  </Text>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={2} align="stretch">
                    {rejected.map((file, index) => (
                      <MediaItem
                        key={index}
                        file={file}
                        type="rejected"
                        onRemove={removeFile}
                        colors={colors}
                        previewUrl={previewUrls.get(file.name) || file.preview}
                      />
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isUploading}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Media Item Component
interface MediaItemProps {
  file: FileWithValidation
  type: 'accepted' | 'rejected'
  onRemove: (fileName: string, type: 'accepted' | 'rejected') => void
  colors: ReturnType<typeof useThemeColors>
  previewUrl?: string | null
}

function MediaItem({ file, type, onRemove, colors, previewUrl: previewUrlProp }: MediaItemProps) {
  // Get preview URL - use prop first (from state Map), then fallback to file.preview
  const previewUrl = previewUrlProp ?? file.preview
  
  // Debug logging
  if (file.type.startsWith('image/') || isImageFile(file)) {
    console.log('MediaItem for image file:', {
      fileName: file.name,
      fileType: file.type,
      previewUrlProp,
      filePreview: file.preview,
      finalPreviewUrl: previewUrl,
      isBlob: previewUrl?.startsWith('blob:'),
    })
  }
  
  // Check if it's a blob URL (image) or null/undefined (other file types)
  const isImagePreview = previewUrl && typeof previewUrl === 'string' && previewUrl.startsWith('blob:')
  
  // Debug: Log the preview check
  if (file.type.startsWith('image/') || isImageFile(file)) {
    console.log('MediaItem preview check:', {
      fileName: file.name,
      previewUrl,
      isImagePreview,
      previewUrlType: typeof previewUrl,
      startsWithBlob: previewUrl?.startsWith('blob:'),
    })
  }

  const renderPreview = () => {
    // If file has a blob URL preview (images), show the actual image
    // Following Dropzone.jsx MediaItem - just use file.preview directly like line 143
    if (isImagePreview && previewUrl) {
      console.log('Rendering image preview for:', file.name, previewUrl)
      return (
        <Box
          boxSize="60px"
          minW="60px"
          minH="60px"
          borderRadius="md"
          bg={colors.border}
          overflow="hidden"
          position="relative"
          flexShrink={0}
          as="img"
          src={previewUrl}
          alt={file.name}
          objectFit="cover"
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            console.error('Image failed to load:', {
              fileName: file.name,
              previewUrl,
              fileType: file.type,
              fileSize: file.size,
              hasPreview: !!file.preview,
              previewType: typeof file.preview,
              filePreview: file.preview,
              error: e,
            })
          }}
          onLoad={() => {
            console.log('Image loaded successfully:', file.name, previewUrl)
          }}
        />
      )
    }
    
    // For non-image files (docs, videos, audio, etc.), use corresponding icon components
    // If we reach here for an image, something went wrong
    if (file.type.startsWith('image/') || isImageFile(file)) {
      console.warn('Image file but no preview URL, showing icon instead:', file.name, {
        previewUrl,
        isImagePreview,
        filePreview: file.preview,
      })
    }
    const iconSize = 24
    
    let IconComponent = File
    if (file.fileType === 'video') {
      IconComponent = Video
    } else if (file.fileType === 'audio') {
      IconComponent = Music
    } else if (file.fileType === 'text' || file.fileType === 'application') {
      IconComponent = FileText
    }

    return (
      <Box
        boxSize="60px"
        bg={colors.border}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={colors.textMuted}
      >
        <IconComponent size={iconSize} />
      </Box>
    )
  }

  return (
    <Box
      p={3}
      border="1px"
      borderColor={type === 'rejected' ? 'red.300' : colors.border}
      borderRadius="md"
      bg={colors.sectionBg}
    >
      <HStack spacing={3} align="flex-start">
        {renderPreview()}
        <VStack align="flex-start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium" color={colors.text} noOfLines={1}>
            {file.name}
          </Text>
          <Text fontSize="xs" color={colors.textMuted}>
            {file.type}
          </Text>
          {file.errors && file.errors.map((error, idx) => (
            <Text key={idx} fontSize="xs" color="red.500">
              {error}
            </Text>
          ))}
          <HStack spacing={2}>
            <Link
              fontSize="xs"
              color="red.500"
              onClick={(e) => {
                e.preventDefault()
                onRemove(file.name, type)
              }}
              _hover={{ textDecoration: 'underline' }}
            >
              <HStack spacing={1}>
                <Trash2 size={12} />
                <Text>Delete</Text>
              </HStack>
            </Link>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  )
}

